import { type ButtonHTMLAttributes, forwardRef, type ReactNode } from 'react';
import cn from 'clsx';
import { cva, type VariantProps } from 'class-variance-authority';
import ArrowThinDownSmall from '@pkgs/components/assets/icons/xsmall/Arrow-Thin-Down-Legacy.svg';
import ArrowTriangleDown from '@pkgs/components/assets/icons/small/ArrowTriangleDown.svg';
import ArrowThinDown from '@pkgs/components/assets/icons/small/Arrow-Thin-Down.svg';
import { type InputTextSizesProps } from '@ev-tech/ui-core-components/InputText';
import { Select as BaseSelect } from '@base-ui/react/select';

import { type ArrowVariant, type SelectButtonVariants, type SelectItem } from './Select.types';

import styles from './Select.module.css';

export const selectButtonVariants = cva(styles.selectButton, {
  variants: {
    variant: {
      basic: styles.basic,
      basicLight: styles.basicLight,
      basicLightV2: styles.basicLightV2,
      outlined: styles.outlined,
      outlinedV2: styles.outlinedV2,
    },
    size: {
      m: styles.sizeM,
      default: '',
    },
    hasError: {
      true: styles.hasError,
      false: '',
    },
  },
  defaultVariants: {
    variant: 'outlined',
    size: 'default',
    hasError: false,
  },
});

type SelectButtonProps = Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> &
  Omit<VariantProps<typeof selectButtonVariants>, 'size'> & {
    buttonIcon?: ReactNode;
    buttonLabelOverride?: string;
    placeholder?: string;
    items: SelectItem[];
    arrowVariant?: ArrowVariant;
    shouldHideArrowIcon?: boolean;
    'data-testid'?: string;
    ariaLabel?: string;
    variant?: SelectButtonVariants;
    size?: InputTextSizesProps;
  };

export const SelectButton = forwardRef<HTMLButtonElement, SelectButtonProps>((props, buttonRef) => {
  const {
    buttonIcon,
    buttonLabelOverride,
    placeholder,
    items,
    size,
    arrowVariant = 'medium',
    shouldHideArrowIcon = false,
    variant = 'outlined',
    hasError = false,
    'data-testid': dataTestId,
    ariaLabel,
    className,
  } = props;

  const renderArrowIcon = () => {
    if (shouldHideArrowIcon) return null;
    if (arrowVariant === 'small') return <ArrowThinDownSmall className={styles.arrowIcon} aria-hidden="true" />;
    if (arrowVariant === 'triangle') return <ArrowTriangleDown className={styles.arrowIcon} aria-hidden="true" />;
    return <ArrowThinDown className={styles.arrowIcon} aria-hidden="true" />;
  };

  return (
    <BaseSelect.Trigger
      data-testid={dataTestId}
      className={cn(selectButtonVariants({ variant, size: size === 'm' ? 'm' : 'default', hasError }), className)}
      ref={buttonRef}
      aria-label={ariaLabel}
    >
      <span className={styles.value}>
        {buttonIcon}
        {buttonLabelOverride ?? (
          <BaseSelect.Value placeholder={placeholder}>
            {(selectedValue: string | null) => {
              const selectedItem = items.find((item) => item.id === selectedValue);
              return selectedItem?.selectedLabel ?? selectedItem?.label ?? placeholder;
            }}
          </BaseSelect.Value>
        )}
      </span>
      {renderArrowIcon()}
    </BaseSelect.Trigger>
  );
});

SelectButton.displayName = 'SelectButton';
